/* global $ toastr */
/**
 * Базовый model view для СИГУР: единые вкладки (DocTabs) и блок кнопок действий
 * в стиле системы. Все model view СИГУР наследуют от него вместо BaseModelView.
 * Кнопки действий — единый вид: outline (как в ДО Территории).
 * Кнопка «Редактировать все» (только для роли admin): временно делает все поля редактируемыми.
 */
import React from 'react'
import { BaseModelView, EventAccess as BaseEventAccess } from '../smart_bps/bps/model_view'
import { request, split_list, format_datetime } from '../smart_bps/lib/utils'
import { store } from '../index'
import { Panel } from '../lib/components'
import AttrInput from '../smart_bps/lib/attr_input'
import DocTabs from './DocTabs'
import { persistDocTab, resolveDocTab } from './docTabPersistence'
import { SigurFilesBlock } from './SigurFilesBlock'
import { RequiredFieldPopover } from './RequiredFieldPopover'
import { SigurPopover } from './SigurPopover'
import { Link } from 'react-router-dom'
import { AuthorizedDropDownMeny, UnauthorizedDropDownMenu } from "./NavDropdownMenu";
import { preloadFormChoices } from './preloadFormChoices'

/** EventAccess с тостерами при выполнении переходов и «Принять». Текст notification показывается как placeholder. Для переходов с обязательным сообщением — ошибка под полем, без тостов. */
class SigurEventAccess extends BaseEventAccess {
  selectEvent(event_name) {
    const that = this
    request({
      method: 'post',
      url: `/api/${this.props.project_name}/${this.props.object_id}/get_event_meta`,
      data: { state_from: this.props.state, event_name },
      success: (data) => {
        that.setState({ selected_event: data, selected_event_message_error: null }, () => {
          $('#modal_event').modal('show')
        })
      },
    })
  }

  renderEventModal() {
    if (this.state === null || this.state.selected_event === undefined) {
      return null
    }
    const event = this.state.selected_event
    const that = this
    const messageError = this.state.selected_event_message_error
    return (
      <div className="modal sigur-event-modal" id="modal_event" tabIndex="-1" style={{ display: 'none' }} aria-hidden="true" key="selected_event">
        <div className="sigur-event-modal__backdrop" data-bs-dismiss="modal" aria-hidden="true" />
        <div className="modal-dialog sigur-event-modal__dialog" role="document">
          <div className="modal-content sigur-event-modal__content">
            <div className="modal-header sigur-event-modal__header">
              <h5 className="modal-title sigur-event-modal__title" id="exampleModalLabel1">{event.title}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Закрыть" />
            </div>
            <div className="modal-body sigur-event-modal__body">
              <div className={messageError ? ' has-error' : ''}>
                <AttrInput
                  data_type="text"
                  rows="5"
                  name="selected_event_message"
                  value={this.state.selected_event_message}
                  access="edit"
                  placeholder={event.notification || undefined}
                  inner_attrs={messageError ? { className: 'is-invalid' } : undefined}
                  onChange={(name, value) => { that.setState({ [name]: value, selected_event_message_error: null }) }}
                />
                {messageError && <div className="invalid-feedback d-block">{messageError}</div>}
              </div>
              {event.change_responsible === 'user' && (
                <div className="mt-2">
                  <AttrInput
                    data_type="integer"
                    name="selected_event_responsible_id"
                    value={this.state.selected_event_responsible_id}
                    choice_items={event.user_choices}
                    access="edit"
                    onChange={(name, value) => { that.setState({ [name]: value }) }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer px-3">
              <button type="button" className="btn btn-primary p-2 me-1" onClick={this.addEvent}>Отправить</button>
              <button type="button" className="btn btn-secondary p-2 me-1" data-bs-dismiss="modal">Закрыть</button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  addEvent() {
    const that = this
    const event = this.state.selected_event
    if (!event) return
    const message = (this.state.selected_event_message || '').trim()
    if (event.notification && !message) {
      this.setState({ selected_event_message_error: 'Обязательное поле' })
      return
    }
    this.setState({ selected_event_message_error: null })
    request({
      method: 'post',
      url: `/api/${this.props.project_name}/${this.props.object_id}/add_event`,
      data: {
        state_from: this.props.state,
        event_name: event.name,
        responsible_id: this.state.selected_event_responsible_id,
        message: this.state.selected_event_message,
      },
      success: (data) => {
        $('#modal_event').modal('hide')
        toastr.success(event.title ? `Переход выполнен: ${event.title}` : 'Переход выполнен')
        that.props.documentToState(data, () => {
          that.setState({
            selected_event: undefined,
            selected_event_responsible_id: undefined,
            selected_event_message: undefined,
            selected_event_message_error: null,
          })
        })
      },
      error: (data) => {
        const msg = data.detail?.detail ?? data.detail
        that.setState({ selected_event_message_error: typeof msg === 'string' ? msg : 'Обязательное поле' })
      },
    })
  }

  renderAcceptEvent() {
    const that = this
    const document_events = that.props.document_events
    const last_event = document_events.length >= 0 ? document_events.at(-1) : undefined
    const isTasksProject = that.props.project_name === 'tasks'
    const showAcceptConfirm = this.state && this.state.showAcceptConfirm

    function acceptEvent() {
      request({
        method: 'post',
        url: `/api/${that.props.project_name}/${that.props.object_id}/accept_event`,
        data: last_event ? { event_id: last_event.id } : {},
        success: (data) => {
          toastr.success('Карточка принята на себя')
          that.props.documentToState(data)
        },
        error: (data) => {
          toastr.error(data.detail || 'Ошибка при принятии карточки')
        },
      })
    }

    if ((last_event || isTasksProject) && this.props.can_event_accept) {
      return (
        <SigurPopover
          visible={!!showAcceptConfirm}
          onClose={() => this.setState({ showAcceptConfirm: false })}
          placement="bottom"
          className="me-2"
          trigger={
            <span
              className="btn btn-outline-success p-2"
              onClick={() => this.setState({ showAcceptConfirm: !showAcceptConfirm })}
            >
              <span className="ti ti-arrow-down me-1"></span>
              Принять
            </span>
          }
        >
          Уверены?{' '}
          <button
            type="button"
            className="btn btn-link p-0 text-primary text-decoration-none"
            onClick={acceptEvent}
          >
            Да, принимаю
          </button>
        </SigurPopover>
      )
    }
  }

  /** Ядро вызывает renderEventsInfo() (Bootstrap #modal_disabled_events); кнопка «Что дальше?» — в SigurModelView справа. */
  render() {
    return [
      this.renderAcceptEvent(),
      this.renderAllowedGroups(),
      this.renderEventModal(),
    ]
  }
}

export class SigurModelView extends BaseModelView {
  constructor(props) {
    super(props)
    this.toggleEditAllMode = this.toggleEditAllMode.bind(this)
    this.exitEditAllMode = this.exitEditAllMode.bind(this)
    this.saveObjectWithEditAllReset = this.saveObjectWithEditAllReset.bind(this)
    this.renderFormAttr = this.renderFormAttr.bind(this)
    this.renderFormGroup = this.renderFormGroup.bind(this)
    this.openDeleteConfirm = this.openDeleteConfirm.bind(this)
    this.closeDeleteConfirm = this.closeDeleteConfirm.bind(this)
    this.confirmDeleteAndClose = this.confirmDeleteAndClose.bind(this)
    this.savePendingFileDates = this.savePendingFileDates.bind(this)
    this.onFileDateSaved = this.onFileDateSaved.bind(this)
    this.renderFileBlock = this.renderFileBlock.bind(this)
    this.fileBlockRef = React.createRef()
    this.openOtherEventsModal = this.openOtherEventsModal.bind(this)
    this.closeOtherEventsModal = this.closeOtherEventsModal.bind(this)
    this.collectDocTabs = this.collectDocTabs.bind(this)
    this.syncDocTabFromLocation = this.syncDocTabFromLocation.bind(this)
    this.selectDocTab = this.selectDocTab.bind(this)
    this.reloadFormChoices = this.reloadFormChoices.bind(this)
  }

  componentDidMount() {
    super.componentDidMount?.()
    this.reloadFormChoices()
  }

  /** Дополнительные choice для предзагрузки (переопределить в наследниках). */
  getExtraChoiceNames() {
    return []
  }

  reloadFormChoices() {
    preloadFormChoices(this.props.attrs, this.getExtraChoiceNames())
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // BaseView: сброс redirect_to после Navigate (без этого — белый экран после сохранения новой карточки).
    super.componentDidUpdate(prevProps, prevState, snapshot)

    const prevRouteId = prevProps.router_params && prevProps.router_params.object_id
    const routeId = this.props.router_params && this.props.router_params.object_id
    if (prevRouteId !== routeId) {
      this.syncDocTabFromLocation()
      return
    }
    if (prevState.object_id !== this.state.object_id && this.state.object_id) {
      this.syncDocTabFromLocation()
    }
  }

  /** Список вкладок карточки. Переопределить, если нужна фильтрация (как в requests/territories). */
  collectDocTabs() {
    const tabs = []
    let first_tab
    const form_schema = this.getFormSchema()
    form_schema.forEach((item) => {
      const tabTitle = item[0]
      if (first_tab === undefined) first_tab = tabTitle
      if (!tabs.includes(tabTitle)) tabs.push(tabTitle)
    })
    return { tabs, first_tab }
  }

  /** Приоритет вкладки при открытии (например, from=complete_approval в requests). */
  getDocTabPreferTab(tabs) {
    return null
  }

  syncDocTabFromLocation() {
    const { tabs, first_tab } = this.collectDocTabs()
    if (!first_tab) return

    if (tabs.length <= 1) {
      if (this.state.selected_tab !== first_tab) {
        this.setState({ selected_tab: first_tab })
      }
      return
    }

    const preferTab = this.getDocTabPreferTab(tabs) || this.state.selected_tab
    const resolved = resolveDocTab({
      tabs,
      firstTab: first_tab,
      preferTab,
      projectName: this.project_name,
      objectId: this.object_id,
    })
    if (resolved && this.state.selected_tab !== resolved) {
      this.setState({ selected_tab: resolved })
      if (this.object_id) {
        persistDocTab(this.project_name, this.object_id, resolved)
      }
    }
  }

  selectDocTab(title) {
    this.setState({ selected_tab: title })
    if (this.object_id) {
      persistDocTab(this.project_name, this.object_id, title)
    }
  }

  getBlockedEvents() {
    const events = Array.isArray(this.state.event_access) ? this.state.event_access : []
    return events.filter(
      (event) => Array.isArray(event.warnings) && event.warnings.filter(Boolean).length > 0
    )
  }

  openOtherEventsModal() {
    this.setState({ showOtherEventsModal: true })
  }

  closeOtherEventsModal() {
    this.setState({ showOtherEventsModal: false })
  }

  renderOtherEventsButton() {
    if (this.getBlockedEvents().length === 0) return null
    return (
      <span
        className="btn btn-outline-secondary p-2"
        onClick={this.openOtherEventsModal}
        key="other_events_btn"
      >
        Что дальше?
      </span>
    )
  }

  renderOtherEventsModal() {
    if (!this.state.showOtherEventsModal) return null
    const blockedEvents = this.getBlockedEvents()
    const groupedEvents = blockedEvents.reduce((acc, event) => {
      const groupName = (event.group || 'Без группы').trim() || 'Без группы'
      if (!acc[groupName]) acc[groupName] = []
      acc[groupName].push(event)
      return acc
    }, {})
    const groupNames = Object.keys(groupedEvents).sort((a, b) => a.localeCompare(b, 'ru'))
    return (
      <div
        className="sigur-delete-confirm-modal modal fade show"
        style={{ display: 'block' }}
        id="modal_other_events"
        key="other_events_modal"
      >
        <div className="sigur-delete-confirm-modal__backdrop" onClick={this.closeOtherEventsModal} aria-hidden="true" />
        <div className="modal-dialog sigur-delete-confirm-modal__dialog" role="document">
          <div className="modal-content sigur-delete-confirm-modal__content">
            <div className="modal-header">
              <h5 className="modal-title">Что дальше?</h5>
              <button type="button" className="btn-close" onClick={this.closeOtherEventsModal} aria-label="Закрыть" />
            </div>
            <div className="modal-body">
              {blockedEvents.length === 0 ? (
                <div className="text-muted">Нет доступных переходов.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {groupNames.map((groupName) => (
                    <div key={`group_${groupName}`} className="border p-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="small text-muted fw-semibold">{groupName}</div>
                        <span className="badge bg-label-secondary">{groupedEvents[groupName].length}</span>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {groupedEvents[groupName].map((event) => {
                          const warnings = (Array.isArray(event.warnings) ? event.warnings : [])
                            .filter(Boolean)
                            .flatMap((warning) =>
                              String(warning)
                                .split('\n')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            )
                          return (
                            <div key={event.name} className="border p-2">
                              <div className="fw-semibold">{event.title || event.name}</div>
                              <ul className="mb-0 mt-2 ps-3">
                                {warnings.map((warning, idx) => (
                                  <li key={`${event.name}_warn_${idx}`} className="small text-muted">
                                    {warning}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary p-2" onClick={this.closeOtherEventsModal}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderTopPanelLeftItems() {
    const items = super.renderTopPanelLeftItems()
      .filter(Boolean)
      .map((item) => {
        return item.key !== 'search' ? item : null
      })
    return items
  }

  /** Заголовок модалки подтверждения удаления. Переопределить во view для своего ДО (например, «Удаление группы доступа»). */
  getDeleteConfirmTitle() {
    return 'Удаление'
  }

  openDeleteConfirm() {
    this.setState({ showDeleteModal: true })
  }

  closeDeleteConfirm() {
    this.setState({ showDeleteModal: false })
  }

  confirmDeleteAndClose() {
    this.setState({ showDeleteModal: false })
    this.deleteObject()
  }

  /** Модальное окно подтверждения удаления: заголовок, иконка, текст, кнопки Удалить (оранжевая) и Отменить (серая). */
  renderDeleteConfirmModal() {
    if (!this.state.showDeleteModal) return null
    return (
      <div
        className="sigur-delete-confirm-modal modal fade show"
        style={{ display: 'block' }}
        id="modal_delete_confirm"
        key="delete_confirm_modal"
      >
        <div className="sigur-delete-confirm-modal__backdrop" onClick={this.closeDeleteConfirm} aria-hidden="true" />
        <div className="modal-dialog sigur-delete-confirm-modal__dialog" role="document">
          <div className="modal-content sigur-delete-confirm-modal__content">
            <div className="modal-header sigur-delete-confirm-modal__header">
              <h5 className="modal-title sigur-delete-confirm-modal__title">{this.getDeleteConfirmTitle()}</h5>
              <button type="button" className="btn-close" onClick={this.closeDeleteConfirm} aria-label="Закрыть" />
            </div>
            <div className="modal-body sigur-delete-confirm-modal__body">
              <span className="sigur-delete-confirm-modal__icon ti ti-alert-triangle" aria-hidden="true" />
              <p className="sigur-delete-confirm-modal__text mb-0">
                Это действие нельзя будет отменить. Вы уверены, что хотите удалить карточку?
              </p>
            </div>
            <div className="modal-footer px-3">
              <button type="button" className="btn btn-primary p-2 me-1" onClick={this.confirmDeleteAndClose}>
                Удалить
              </button>
              <button type="button" className="btn btn-secondary p-2 me-1" onClick={this.closeDeleteConfirm}>
                Отменить
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  get isAdmin() {
    return this.props.user && this.props.user.roles && this.props.user.roles.includes('admin')
  }

  get isResponsible() {
    return this.props.user?.id == this.state.responsible_id
  }

  /** Схема формы: только вкладки, у которых хотя бы одно поле есть в form_access (видимость через form_access, без отдельной схемы на документ). */
  getFormSchema() {
    const schema = this.props.form_schema || []
    const accessKeys = Object.keys(this.state.form_access || {})
    if (accessKeys.length === 0) return schema
    return schema.filter((item) => {
      const attrNames = item[2] || []
      return attrNames.some((name) => accessKeys.includes(name))
    })
  }

  documentToState(data, cb) {
    this.reloadFormChoices()
    const state_data = { ...data }
    state_data.form_data_initial = data.form_data
    state_data.object_id = data.id
    state_data.form_errors = undefined
    if (this.object_id === 0 && data.id !== 0) {
      state_data.redirect_to = `/${this.project_name}/${data.id}`
      if (this.state.selected_tab) {
        persistDocTab(this.project_name, data.id, this.state.selected_tab)
      }
    }
    this.setState(state_data, () => {
      this.syncDocTabFromLocation()
      if (cb) cb()
    })
  }

  /** Перезагрузка документа с form_access = get_form_access_full('edit') и включение режима «Редактировать все». */
  toggleEditAllMode() {
    this.getDocument(true)
  }

  /** Перезагрузка документа без force_edit_all — возврат обычного form_access и выключение режима. */
  exitEditAllMode() {
    this.getDocument(false)
  }

  /** get_document с опцией force_edit_all: перезагрузка с form_access = все поля "edit". */
  getDocument(forceEditAll = false) {
    const that = this
    const url = `/api/${this.project_name}/${this.object_id}/get_document` +
      (forceEditAll ? '?force_edit_all=true' : '')
    request({
      method: 'get',
      url,
      success: (data) => {
        that.waitRenderReadyLock = false
        that.documentToState(data)
        that.setState({
          edit_all_mode: !!forceEditAll,
        })
      },
      error: () => {
        that.waitRenderReadyLock = false
        toastr.error('Ошибка в получении данных')
      },
    })
  }

  async savePendingFileDates() {
    const fileBlockRef = this.fileBlockRef.current
    if (!fileBlockRef || typeof fileBlockRef.saveAllEditedFileDates !== 'function') return true
    return await fileBlockRef.saveAllEditedFileDates()
  }

  /** Сохранение с отключением режима «Редактировать все» после успеха. */
  saveObjectWithEditAllReset() {
    const that = this
    // В режиме «Редактировать все» отправляем все ключи; undefined → null, иначе при очистке поля
    // ключ не попадёт в JSON и бэкенд не обнулит значение.
    that.savePendingFileDates().then((result) => {
      const isSavedFiles = typeof result === 'object' ? result.success : result
      const toastrMsg = result?.toastrMsg
      let new_data = that.state.form_data

      if (that.state.edit_all_mode && that.state.form_data_initial) {
        new_data = Object.fromEntries(
          Object.entries({ ...that.state.form_data_initial, ...new_data }).map(([k, v]) => [
            k,
            v === undefined ? null : v,
          ])
        )
      }

      request({
        method: 'post',
        url: `/api/${that.project_name}/${that.object_id}/update_document`,
        data: {
          new_data,
          old_data: that.state.form_data_initial,
          force_edit_all: !!that.state.edit_all_mode,
        },
        success: (data) => {
          store.dispatch({ type: `${that.project_name}_list_clean` })
          if (data.document && isSavedFiles) {
            that.documentToState(data.document)
            that.setState({ edit_all_mode: false })
            toastr.success('Изменения сохранены')
          }
          if (data.errors || !isSavedFiles) {
            that.setState({ form_errors: data.errors })
            let customErrors = []
            if (data && typeof data.errors === 'object' && data.errors._document_dates_error) customErrors.push(data.errors._document_dates_error)
            if (toastrMsg) customErrors.push(toastrMsg)

            if (!customErrors.length) toastr.warning('Изменения не были сохранены. Нужно исправить ошибки в форме документа', 'Внимание');

            customErrors.forEach((msg) => {
                toastr.warning(
                msg || 'Изменения не были сохранены. Нужно исправить ошибки в форме документа',
                'Внимание'
              );
            })
          }
        },
      })
    })
  }

  renderTabPanel() {
    const { tabs, first_tab } = this.collectDocTabs()
    if (tabs.length <= 1) return null

    return (
      <DocTabs
        tabs={tabs}
        selectedTab={this.state.selected_tab || first_tab}
        onSelect={(title) => this.selectDocTab(title)}
      />
    )
  }

  renderContentLeftAction() {
    const leftItems = (this.renderContentLeftActionLeftItems() || []).filter(Boolean).filter((action) => {
      // Если нет ни переходов, ни кнопки "Принять" — не оставляем пустой контейнер событий.
      if (action?.key === 'events') {
        const hasEvents = Array.isArray(this.state.event_access) && this.state.event_access.length > 0
        const canAccept = !!this.state.can_event_accept
        return hasEvents || canAccept
      }
      return true
    })
    const rightItems = (this.renderContentLeftActionRightItems() || []).filter(Boolean)
    const hasActions = leftItems.length > 0 || rightItems.length > 0

    return (
      <>
        {hasActions && (
          <div className="card mb-3 sigur-doc-actions" key="actions" data-key="actions">
            <div className="card-body p-2 d-flex flex-wrap align-items-center justify-content-between">
              <div className="d-flex flex-wrap align-items-center gap-1">{leftItems}</div>
              <div className="d-flex flex-wrap align-items-center gap-1 ms-auto">{rightItems}</div>
            </div>
          </div>
        )}
        {this.renderDeleteConfirmModal()}
        {this.renderOtherEventsModal()}
      </>
    )
  }

  onFileDateSaved(item) {
    this.setState((s) => {
      const existing = s.file_dates || []

      const idx = existing.findIndex(
        (f) => f.file_id === item.file_id
      )

      if (idx >= 0) {
        const next = [...existing]
        next[idx] = item
        return { file_dates: next }
      }

      return { file_dates: [...existing, item] }
    })
  }

  renderFileBlock(isRenderDataFields = false) {
    const filesSchema = this.state.files_schema || []
    const fileAccess = this.state.file_access
    const fileAccessByType = this.state.file_access_by_type
    if (fileAccess && fileAccess.read === false) {
      return null
    }
    if (filesSchema.length > 0) {
      return (
        <SigurFilesBlock
          key="files"
          ref={this.fileBlockRef}
          project_name={this.project_name}
          object_id={this.object_id}
          files_schema={filesSchema}
          files={this.state.files}
          fileUploaded={this.fileUploaded}
          getChoice={this.getChoice}
          onFileDateSaved={this.onFileDateSaved}
          isRenderDataFields={isRenderDataFields}
          isRenderChevron={true}
          documetSetState={this.documentToState}
          fileAccess={fileAccess}
          fileAccessByType={fileAccessByType}
          viewOnly={fileAccess ? !fileAccess.upload : undefined}
          currentUserId={this.props.user?.id}
        />
      )
    }
  }

  /** Правая колонка: инфо, блок «Файлы» (общий для проекта), комментарии. */
  renderContentRight() {
    if (this.object_id === 0) {
      return []
    }
    const filesSchema = this.state.files_schema || []
    const fileAccess = this.state.file_access
    const blocks = [this.renderInfo()]
    if (filesSchema.length > 0 && (!fileAccess || fileAccess.read !== false)) {
      blocks.push(
        this.renderFileBlock()
      )
    }
    blocks.push(this.renderComments())
    return blocks
  }

  /** Левый блок кнопок панели действий. */
  renderContentLeftActionLeftItems() {
    return [
      <span
        className="btn btn-outline-primary waves-effect p-2"
        onClick={this.saveObjectWithEditAllReset}
        key="save"
      >
        Сохранить
      </span>,

      <span key="events" className="sigur-event-access d-inline-flex align-items-center">
        <SigurEventAccess
          event_access={this.state.event_access}
          project_name={this.project_name}
          object_id={this.object_id}
          can_event_accept={this.state.can_event_accept}
          document_events={this.state.events}
          state={this.state.state}
          documentToState={this.documentToState}
        />
      </span>,

      this.state.perms && this.state.perms.includes('delete') && (
        <span
          className="btn btn-outline-danger waves-effect p-2"
          onClick={this.openDeleteConfirm}
          key="delete"
        >
          Удалить
        </span>
      ),
    ]
  }

  /** Правый блок кнопок панели действий. */
  renderContentLeftActionRightItems() {
    const editAllMode = this.state.edit_all_mode
    const showEditAll = this.isAdmin && !editAllMode
    const showExitEditAll = this.isAdmin && editAllMode

    return [
      this.renderOtherEventsButton(),

      showEditAll && (
        <span
          className="btn btn-outline-secondary waves-effect p-2"
          onClick={this.toggleEditAllMode}
          key="edit_all"
        >
          Редактировать все
        </span>
      ),
      showExitEditAll && (
        <span
          className="btn btn-outline-warning waves-effect p-2"
          onClick={this.exitEditAllMode}
          key="exit_edit_all"
        >
          Не редактировать все
        </span>
      ),
    ]
  }

  /** Доступ к полю: в режиме «Редактировать все» — всегда edit. У обязательных полей — оранжевая иконка «i» (как в ПИМУ). */
  renderFormAttr(attr, horizontal) {
    const form_access = this.state.form_access || {}
    const access = this.state.edit_all_mode ? 'edit' : form_access[attr.name]
    const data = this.state.form_data
    const attr_error = this.state.form_errors && this.state.form_errors[attr.name]
    const attr_message = this.state.form_messages && this.state.form_messages[attr.name]
    const requiredFields = this.state.required_fields || this.props.required_fields || []
    const required = (Array.isArray(requiredFields) && requiredFields.includes(attr.name)) || attr.required === "true"
    const title = required && attr.title
      ? (
          <>
            {attr.title}{' '}
            <RequiredFieldPopover />
          </>
        )
      : attr.title

    return (
      <AttrInput
        key={attr.name}
        data_type={attr.data_type}
        widget={attr.widget}
        choice_name={attr.choice_name}
        choices={this.props.choices}
        name={attr.name}
        title={title}
        value={data[attr.name]}
        access={access}
        message={attr_message}
        error={attr_error}
        inner_attrs={attr.inner_attrs}
        horizontal={horizontal || false}
        tags={false}
        onChange={this.formChange}
      />
    )
  }

  /** В режиме «Редактировать все» показываем все поля группы (не только с доступом). */
  renderContentLeft() {
    const form_schema = this.getFormSchema()
    return [
      this.renderContentLeftAction(),
      this.renderNotifications(),
      this.renderTabPanel(),
      form_schema.map((item, idx) => this.renderFormGroup(item, idx)),
      this.renderEventBlock(),
    ]
  }

  renderEventBlock() {
    if (this.object_id === 0) {
      return null;
    }

    return (
      <Panel header="События по документу" key="events" collapsed={true}>
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Создано</th>
              <th>Событие</th>
              <th>Ответственный</th>
            </tr>
          </thead>
          <tbody>
            <tr key="head">
              <td>
                {format_datetime(this.state.d_create)} {" "}
                <span className="text-muted">{this.getChoice("User", this.state.author_id)}</span>
              </td>
              <td>Создание</td>
              <td>---</td>
            </tr>
            {this.state.events.length > 0 && this.state.events.map((event, idx) => {
              return <tr key={idx}>
                <td>
                  {format_datetime(event.d_create)} {" "}
                  <span className="text-muted">{this.getChoice("User", event.author_id)}</span>
                </td>
                <td>
                  {event.title}: <span className="text-muted">{event.message}</span>
                </td>
                <td>
                  <span className="text-muted">{this.getChoice("User", event.responsible_id)}</span>
                </td>
              </tr>
            })}
          </tbody>
        </table>
      </Panel>
    )
  }

  renderComments() {
    const that = this
    const perms = this.state.perms || []
    const can_add_comment = perms.includes('add_comment')
    const comments = this.state.comments || []

    if (!can_add_comment && comments.length === 0) return null

    function getInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.slice(0, 2).toUpperCase()
    }

    const addComment = () => {
      const msg = (that.state.comment_form_message || '').trim()
      if (!msg) return
      request({
        method: 'post',
        url: `/api/${that.project_name}/${that.object_id}/add_comment`,
        data: { message: msg },
        success: (data) => {
          that.setState({
            comment_form_message: '',
            comments: [...comments, data],
          })
        },
      })
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        addComment()
      }
    }

    const commentsBlockExpanded = this.state.commentsBlockExpanded !== false
    const toggleCommentsBlock = () => {
      this.setState((s) => ({ commentsBlockExpanded: (s.commentsBlockExpanded !== false) ? false : true }))
    }
    const collapseIconClass = commentsBlockExpanded ? 'ti-chevron-down' : 'ti-chevron-left'

    return (
      <div className="card card-action mb-3 sigur-comments" key="comments">
        <div className="card-header py-2 px-3">
          <h5 className="card-action-title mb-0">Комментарии</h5>
          <div className="card-action-element d-flex align-items-center gap-2">
            {comments.length > 0 && (
              <span className="sigur-comments__count">{comments.length}</span>
            )}
            <ul className="list-inline mb-0 ms-auto">
              <li className="list-inline-item">
                <span
                  className="card-collapsible pointer"
                  onClick={toggleCommentsBlock}
                  aria-expanded={commentsBlockExpanded}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCommentsBlock() } }}
                >
                  <i className={`tf-icons ti scaleX-n1-rtl ti-sm ${collapseIconClass}`} aria-hidden />
                </span>
              </li>
            </ul>
          </div>
        </div>
        {commentsBlockExpanded && (
        <div className="card-body p-0">
          <div className="sigur-comments__section">
            <div className="sigur-comments__section-body">
              {comments.length === 0 && (
                <div className="sigur-comments__empty">
                  <i className="ti ti-message-off" aria-hidden />
                  <span>Нет комментариев</span>
                </div>
              )}
              {comments.length > 0 && (
                <div className="sigur-comments__list">
                  {comments.map((item) => {
                    const authorName = item.author_name || this.getChoice('User', item.author_id) || '?'
                    return (
                      <div className="sigur-comments__row" key={item.id}>
                        <div className="sigur-comments__avatar" title={authorName}>
                          {getInitials(authorName)}
                        </div>
                        <div className="sigur-comments__info">
                          <div className="sigur-comments__meta">
                            <span className="sigur-comments__author">{authorName}</span>
                            <span className="sigur-comments__date">
                              {item.d_create ? format_datetime(item.d_create) : '—'}
                            </span>
                          </div>
                          <div className="sigur-comments__text">{item.message}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              {can_add_comment && (
                <div className="sigur-comments__form">
                  <textarea
                    className="sigur-comments__input"
                    rows="2"
                    placeholder="Написать комментарий…"
                    value={this.state.comment_form_message || ''}
                    onChange={(e) => that.setState({ comment_form_message: e.target.value })}
                    onKeyDown={handleKeyDown}
                  />
                  <div className="sigur-comments__form-actions">
                    <span className="sigur-comments__hint">Ctrl+Enter</span>
                    <button
                      type="button"
                      className="btn btn-sm sigur-comments__send"
                      onClick={addComment}
                      disabled={!(this.state.comment_form_message || '').trim()}
                      title="Отправить"
                    >
                      <i className="ti ti-send me-1" aria-hidden />
                      Отправить
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    )
  }

  renderFormGroup(item, idx) {
    const selected_tab = this.state.selected_tab
    const tab_title = item[0]
    const group_title = item[1]
    const attr_names = item[2]
    if (tab_title !== selected_tab) return null

    const grCollapsed = (this.props.form_group_hidden || []).includes(group_title)
    const form_access = this.state.form_access || {}
    const attrs = this.props.attrs || {}
    const form_access_names = Object.keys(form_access)
    const editAllMode = this.state.edit_all_mode
    const attr_names_allowed = editAllMode
      ? attr_names.filter((name) => attrs[name])
      : attr_names.filter((name) => form_access_names.includes(name))

    if (attr_names_allowed.length === 0) return null

    let wide_attrs = []
    let short_attrs = []
    attr_names_allowed.forEach((attr_name) => {
      const attr = attrs[attr_name]
      if (!attr) return
      if (attr.data_type === 'table' || (attr.tags && attr.tags.includes('form_wide'))) {
        wide_attrs.push(attr)
      } else {
        short_attrs.push(attr)
      }
    })

    const toggleShow = (show) => {
      store.dispatch({ type: `${this.project_name}_block_toggle`, data: { title: group_title, show } })
    }

    return (
      <Panel header={group_title} key={idx} collapsed={grCollapsed} toggleShow={toggleShow}>
        <div className="row form-sm">
          {this.renderFormGroupInclude(item, 'top')}
          {short_attrs.length > 0 &&
            split_list(short_attrs, 2).map((rowAttrs, rowIdx) => (
              <div className="col-md-6" key={rowIdx}>
                {rowAttrs.map((attr) => this.renderFormAttr(attr, true))}
              </div>
            ))}
          {wide_attrs.length > 0 && (
            <div className="col-md-12" key="wide">
              {wide_attrs.map((attr) => this.renderFormAttr(attr, false))}
            </div>
          )}
          {this.renderFormGroupInclude(item, 'bottom')}
        </div>
      </Panel>
    )
  }

  renderBreadcrumbs() {
    const rawTitle = this.state && this.state.title != null ? String(this.state.title) : ''
    const safeTitle = rawTitle.replace('#None', '#---')
    return (
      <nav aria-label="breadcrumb" key="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Домой</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/${this.project_name}`}>{this.props.project_title}</Link>
          </li>
          <li className="breadcrumb-item active">{safeTitle}</li>
        </ol>
      </nav>
    )
  }

  renderTopPanelRightItems() {
    if (this.authorized) {
      return (
        <AuthorizedDropDownMeny
          userTitle={this.user_title}
          onLogout={this.logout}
        />
      )
    }
    return <UnauthorizedDropDownMenu/>
  }
}
